/**
 * Per-user/per-organisation Pricelist IQ preference.
 *
 * The backend scopes UserViewPreference by the authenticated user and active
 * organisation. This module deliberately has no browser-storage fallback and
 * never elevates catalogue requests with query parameters.
 */
import { useCallback, useEffect, useSyncExternalStore } from 'react'

export const PRICELIST_IQ_SCOPE = 'pricelist_iq'
const VIEW_PREFERENCE_URL = '/api/settings/view-preferences/'

export interface PricelistIqSnapshot {
  enabled: boolean
  hydrated: boolean
  loading: boolean
  saving: boolean
}

export interface PricelistIqClient {
  load(): Promise<unknown>
  save(enabled: boolean): Promise<void>
}

interface HttpLike {
  get(url: string, config?: { params?: Record<string, unknown> }): Promise<{ data: unknown }>
  patch(url: string, body?: unknown): Promise<{ data: unknown }>
}

let snapshot: PricelistIqSnapshot = {
  enabled: false,
  hydrated: false,
  loading: false,
  saving: false,
}
let mutationVersion = 0
const listeners = new Set<() => void>()

const emit = () => listeners.forEach((listener) => listener())
const setSnapshot = (next: PricelistIqSnapshot) => {
  snapshot = next
  emit()
}

export function createPricelistIqClient(http: HttpLike): PricelistIqClient {
  return {
    async load() {
      const { data } = await http.get(VIEW_PREFERENCE_URL, {
        params: { scope_key: PRICELIST_IQ_SCOPE },
      })
      return (data as { state?: unknown } | null)?.state
    },
    async save(enabled) {
      await http.patch(VIEW_PREFERENCE_URL, {
        scope_key: PRICELIST_IQ_SCOPE,
        state: { enabled },
      })
    },
  }
}

export function getPricelistIqSnapshot(): PricelistIqSnapshot {
  return snapshot
}

export function subscribePricelistIq(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export async function loadPricelistIq(client: PricelistIqClient): Promise<void> {
  if (snapshot.loading) return
  const version = mutationVersion
  setSnapshot({ ...snapshot, loading: true })
  try {
    const state = await client.load()
    if (version !== mutationVersion) return
    const enabled =
      typeof state === 'object' &&
      state !== null &&
      (state as { enabled?: unknown }).enabled === true
    setSnapshot({ enabled, hydrated: true, loading: false, saving: snapshot.saving })
  } catch {
    if (version !== mutationVersion) return
    setSnapshot({ enabled: false, hydrated: true, loading: false, saving: snapshot.saving })
  }
}

export async function updatePricelistIq(
  client: PricelistIqClient,
  enabled: boolean,
): Promise<void> {
  mutationVersion += 1
  const previous = snapshot.enabled
  setSnapshot({ ...snapshot, enabled, hydrated: true, saving: true })
  try {
    await client.save(enabled)
    setSnapshot({ ...snapshot, enabled, hydrated: true, saving: false })
  } catch (error) {
    setSnapshot({ ...snapshot, enabled: previous, hydrated: true, saving: false })
    throw error
  }
}

export function usePricelistIqSnapshot(): PricelistIqSnapshot {
  return useSyncExternalStore(
    subscribePricelistIq,
    getPricelistIqSnapshot,
    getPricelistIqSnapshot,
  )
}

export function usePricelistIq(client: PricelistIqClient) {
  const state = usePricelistIqSnapshot()
  useEffect(() => {
    if (!state.hydrated && !state.loading) void loadPricelistIq(client)
  }, [client, state.hydrated, state.loading])
  const setEnabled = useCallback(
    (enabled: boolean) => updatePricelistIq(client, enabled),
    [client],
  )
  return { ...state, setEnabled }
}

export function resetPricelistIqState(): void {
  mutationVersion += 1
  setSnapshot({
    enabled: false,
    hydrated: false,
    loading: false,
    saving: false,
  })
}
